import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha, makeStyles } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'PatientName',
    numeric: false,
    disablePadding: false,
    label: 'Patient Name',
    title: '',
  },
  {
    id: 'TestDate',
    numeric: true,
    disablePadding: false,
    label: 'Test Date',
    title: '',
  },
  // {
  //     id: 'PatientPhone',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Phone Number',
  // },
  {
    id: 'age',
    numeric: true,
    disablePadding: false,
    label: 'Age',
    title: '',
  },
  {
    id: 'Gender',
    numeric: true,
    disablePadding: false,
    label: 'Gender',
    title: '',
  },
  // {
  //     id: 'HealthScore',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Total Score',
  // },
  {
    id: 'Total_SCORE',
    numeric: true,
    disablePadding: false,
    label: 'Health Score',
    title: '',
  },
  {
    id: 'Weight',
    numeric: true,
    disablePadding: false,
    label: 'Weight (kg)',
    title: '',
  },
  {
    id: 'Height',
    numeric: true,
    disablePadding: false,
    label: 'Height (cm)',
    title: '',
  },
  {
    id: 'BMI',
    numeric: true,
    disablePadding: false,
    label: 'BMI',
    title: `<p>Body Mass Index (BMI) is a person's weight in kilograms (or pounds) divided by the square of height in meters (or
            feet). A high BMI can indicate high body fatness. BMI screens for weight categories that may lead to health
            problems, but it does not diagnose the body fatness or health of an individual.</p>
            <p>Formula</p>
           <p> BMI = Weight (kg) ÷ Height 2 (meter 2) </p>`,
  },
  {
    id: 'BMR',
    numeric: true,
    disablePadding: false,
    label: 'BMR (kcal)',
    title: `<p>Your Basal Metabolic Rate (BMR) is the number of calories you burn as your body performs basic (basal) life-
        sustaining function. Commonly also termed as Resting Metabolic Rate (RMR), which is the calories burned if you
        stayed in bed all day.</p>
        <p>Formula</p>
        <p>BMR For Men BMR (metric) = (10 x weight in kg) + (6.25 x height in cm) - (5 x age in years) + 5 </p>
        <p>BMR For Women BMR (metric) = (10  x weight in kg) + (6.25 x height in cm) - (5 x age in years) - 161 </p>`,
  },
  {
    id: 'LBM',
    numeric: true,
    disablePadding: false,
    label: 'LBM (kg)',
    title: `<p>Lean body mass (LBM), sometimes conflated with fat-free mass, is a component of body composition. Fat-free mass
        (FFM) is calculated by subtracting body fat weight from total body weight: total body weight is lean plus fat.</p>
        <p>Formula</p>
        <p>LBM For Men LBM = (0.407 x weight in kg) + (0.267 x height in cm) - 19.2</p>
        <p>LBM For Women LBM = (0.252 x weight in kg) + (0.473 x height in cm) - 48.3</p>
        <p>LBM% = (LBM kg * weight in kg) * 100 </p>`,
  },

  {
    id: 'BodyFatWeight',
    numeric: true,
    disablePadding: false,
    label: 'Body Fat Weight (%)',
    title: `<p>Body fat (BF) is the number of fatty muscles in the body, which represents the total fatty tissue that does not contain
        any muscle mass MM, electrolyte, or body fluids (Kyle, Bosaeus, et al., 2004b) (Bera, 2014). It is the (TBM — FFM).</p>
        <p>Formula</p>
        <p>BF% = (body weight Kg - lean body mass(KG))/(body weight kg) X 100</p>`,
  },
  {
    id: 'MuscleMass',
    numeric: true,
    disablePadding: false,
    label: 'Muscle Mass (%)',
    title: `<p>Muscle mass includes the weight of the muscles in your body in kilograms or pounds. While the body fat percentage
        measures the amount of fat your body holds. Burning body fat can be a result of the accumulated muscles. Muscles
        are like engines when it comes to consuming energy</p>
        <p>Formula</p>
        <p>fat free mass = weight [kg] * (1 - (body fat [%]/ 100)) expressed in kilogram units [kg].</p>
        <p>FFMI = fat free mass [kg]/ (height [m])². Expressed in kilograms per square meter [kg/m²].</p>`,
  },
  {
    id: 'VisceralFat',
    numeric: true,
    disablePadding: false,
    label: 'Visceral Fat (%)',
    title: `<p>The normal visceral fat range should be about 10% of your body fat.
         You can figure out your visceral fat level by calculating your total body fat percentage and then taking off 10%.</p>`,
  },
  {
    id: 'SubcutaneousFat',
    numeric: true,
    disablePadding: false,
    label: 'Subcutaneous Fat (%)',
    title: `<p>For most people, subcutaneous fat makes up about 90% of their body fat. The remaining 10% makes up visceral fat.</p>`,
  },
];

const headCells1 = [
  {
    id: 'PatientName',
    numeric: false,
    disablePadding: false,
    label: 'Patient Name',
    title: '',
  },
  {
    id: 'TestDate',
    numeric: true,
    disablePadding: false,
    label: 'Test Date',
    title: '',
  },
  // {
  //     id: 'PatientPhone',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Phone Number',
  // },
  {
    id: 'age',
    numeric: true,
    disablePadding: false,
    label: 'Age',
    title: '',
  },
  {
    id: 'Gender',
    numeric: true,
    disablePadding: false,
    label: 'Gender',
    title: '',
  },
  // {
  //     id: 'HealthScore',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Total Score',
  // },
  {
    id: 'SickleCell_SOL',
    numeric: true,
    disablePadding: false,
    label: 'Sickle Solubility',
    title: '',
  },
  {
    id: 'SickleCell_SCORE_TEXT',
    numeric: true,
    disablePadding: false,
    label: 'Sickle Cell RDT',
    title: '',
  },
  {
    id: 'HEMOGLOBIN',
    numeric: true,
    disablePadding: false,
    label: 'Hemoglobin',
    title: '',
  },
  {
    id: 'HealthScore',
    numeric: true,
    disablePadding: false,
    label: 'Non Invasive',
    title: '',
  },

  // {
  //     id: 'Total_SCORE',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Health Score',
  //     title:''
  // },
  // {
  //     id: 'Weight',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Weight (kg)',
  //     title:''
  // },
  // {
  //     id: 'Height',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Height (cm)',
  //     title:''
  // },
  {
    id: 'BMI',
    numeric: true,
    disablePadding: false,
    label: 'BMI',
    title: `<p>Body Mass Index (BMI) is a person's weight in kilograms (or pounds) divided by the square of height in meters (or
            feet). A high BMI can indicate high body fatness. BMI screens for weight categories that may lead to health
            problems, but it does not diagnose the body fatness or health of an individual.</p>
            <p>Formula</p>
           <p> BMI = Weight (kg) ÷ Height 2 (meter 2) </p>`,
  },
  {
    id: 'BMR',
    numeric: true,
    disablePadding: false,
    label: 'BMR (kcal)',
    title: `<p>Your Basal Metabolic Rate (BMR) is the number of calories you burn as your body performs basic (basal) life-
        sustaining function. Commonly also termed as Resting Metabolic Rate (RMR), which is the calories burned if you
        stayed in bed all day.</p>
        <p>Formula</p>
        <p>BMR For Men BMR (metric) = (10 x weight in kg) + (6.25 x height in cm) - (5 x age in years) + 5 </p>
        <p>BMR For Women BMR (metric) = (10  x weight in kg) + (6.25 x height in cm) - (5 x age in years) - 161 </p>`,
  },
  {
    id: 'LBM',
    numeric: true,
    disablePadding: false,
    label: 'LBM (kg)',
    title: `<p>Lean body mass (LBM), sometimes conflated with fat-free mass, is a component of body composition. Fat-free mass
        (FFM) is calculated by subtracting body fat weight from total body weight: total body weight is lean plus fat.</p>
        <p>Formula</p>
        <p>LBM For Men LBM = (0.407 x weight in kg) + (0.267 x height in cm) - 19.2</p>
        <p>LBM For Women LBM = (0.252 x weight in kg) + (0.473 x height in cm) - 48.3</p>
        <p>LBM% = (LBM kg * weight in kg) * 100 </p>`,
  },

  {
    id: 'BodyFatWeight',
    numeric: true,
    disablePadding: false,
    label: 'Body Fat Weight (%)',
    title: `<p>Body fat (BF) is the number of fatty muscles in the body, which represents the total fatty tissue that does not contain
        any muscle mass MM, electrolyte, or body fluids (Kyle, Bosaeus, et al., 2004b) (Bera, 2014). It is the (TBM — FFM).</p>
        <p>Formula</p>
        <p>BF% = (body weight Kg - lean body mass(KG))/(body weight kg) X 100</p>`,
  },
  {
    id: 'MuscleMass',
    numeric: true,
    disablePadding: false,
    label: 'Muscle Mass (%)',
    title: `<p>Muscle mass includes the weight of the muscles in your body in kilograms or pounds. While the body fat percentage
        measures the amount of fat your body holds. Burning body fat can be a result of the accumulated muscles. Muscles
        are like engines when it comes to consuming energy</p>
        <p>Formula</p>
        <p>fat free mass = weight [kg] * (1 - (body fat [%]/ 100)) expressed in kilogram units [kg].</p>
        <p>FFMI = fat free mass [kg]/ (height [m])². Expressed in kilograms per square meter [kg/m²].</p>`,
  },
  {
    id: 'VisceralFat',
    numeric: true,
    disablePadding: false,
    label: 'Visceral Fat (%)',
    title: `<p>The normal visceral fat range should be about 10% of your body fat.
         You can figure out your visceral fat level by calculating your total body fat percentage and then taking off 10%.</p>`,
  },
  {
    id: 'SubcutaneousFat',
    numeric: true,
    disablePadding: false,
    label: 'Subcutaneous Fat (%)',
    title: `<p>For most people, subcutaneous fat makes up about 90% of their body fat. The remaining 10% makes up visceral fat.</p>`,
  },
];

const headCells2 = [
  {
    id: 'PatientName',
    numeric: false,
    disablePadding: false,
    label: 'Patient Name',
    title: '',
  },
  {
    id: 'TestDate',
    numeric: true,
    disablePadding: false,
    label: 'Test Date',
    title: '',
  },
  // {
  //     id: 'PatientPhone',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Phone Number',
  // },
  {
    id: 'age',
    numeric: true,
    disablePadding: false,
    label: 'Age',
    title: '',
  },
  {
    id: 'Gender',
    numeric: true,
    disablePadding: false,
    label: 'Gender',
    title: '',
  },
  // {
  //     id: 'HealthScore',
  //     numeric: true,
  //     disablePadding: false,
  //     label: 'Total Score',
  // },
  {
    id: 'SickleCell_SOL',
    numeric: true,
    disablePadding: false,
    label: 'Sickle Solubility',
    title: '',
  },
  {
    id: 'SickleCell_SCORE_TEXT',
    numeric: true,
    disablePadding: false,
    label: 'Sickle Cell RDT',
    title: '',
  },
  {
    id: 'HEMOGLOBIN',
    numeric: true,
    disablePadding: false,
    label: 'Hemoglobin',
    title: '',
  },
  {
    id: 'HealthScore',
    numeric: true,
    disablePadding: false,
    label: 'Non Invasive',
    title: '',
  },

  {
    id: 'Total_SCORE',
    numeric: true,
    disablePadding: false,
    label: 'Health Score',
    title: '',
  },
  {
    id: 'Weight',
    numeric: true,
    disablePadding: false,
    label: 'Weight (kg)',
    title: '',
  },
  {
    id: 'Height',
    numeric: true,
    disablePadding: false,
    label: 'Height (cm)',
    title: '',
  },
  {
    id: 'BMI',
    numeric: true,
    disablePadding: false,
    label: 'BMI',
    title: `<p>Body Mass Index (BMI) is a person's weight in kilograms (or pounds) divided by the square of height in meters (or
            feet). A high BMI can indicate high body fatness. BMI screens for weight categories that may lead to health
            problems, but it does not diagnose the body fatness or health of an individual.</p>
            <p>Formula</p>
           <p> BMI = Weight (kg) ÷ Height 2 (meter 2) </p>`,
  },
  {
    id: 'BMR',
    numeric: true,
    disablePadding: false,
    label: 'BMR (kcal)',
    title: `<p>Your Basal Metabolic Rate (BMR) is the number of calories you burn as your body performs basic (basal) life-
        sustaining function. Commonly also termed as Resting Metabolic Rate (RMR), which is the calories burned if you
        stayed in bed all day.</p>
        <p>Formula</p>
        <p>BMR For Men BMR (metric) = (10 x weight in kg) + (6.25 x height in cm) - (5 x age in years) + 5 </p>
        <p>BMR For Women BMR (metric) = (10  x weight in kg) + (6.25 x height in cm) - (5 x age in years) - 161 </p>`,
  },
  {
    id: 'LBM',
    numeric: true,
    disablePadding: false,
    label: 'LBM (kg)',
    title: `<p>Lean body mass (LBM), sometimes conflated with fat-free mass, is a component of body composition. Fat-free mass
        (FFM) is calculated by subtracting body fat weight from total body weight: total body weight is lean plus fat.</p>
        <p>Formula</p>
        <p>LBM For Men LBM = (0.407 x weight in kg) + (0.267 x height in cm) - 19.2</p>
        <p>LBM For Women LBM = (0.252 x weight in kg) + (0.473 x height in cm) - 48.3</p>
        <p>LBM% = (LBM kg * weight in kg) * 100 </p>`,
  },

  {
    id: 'BodyFatWeight',
    numeric: true,
    disablePadding: false,
    label: 'Body Fat Weight (%)',
    title: `<p>Body fat (BF) is the number of fatty muscles in the body, which represents the total fatty tissue that does not contain
        any muscle mass MM, electrolyte, or body fluids (Kyle, Bosaeus, et al., 2004b) (Bera, 2014). It is the (TBM — FFM).</p>
        <p>Formula</p>
        <p>BF% = (body weight Kg - lean body mass(KG))/(body weight kg) X 100</p>`,
  },
  {
    id: 'MuscleMass',
    numeric: true,
    disablePadding: false,
    label: 'Muscle Mass (%)',
    title: `<p>Muscle mass includes the weight of the muscles in your body in kilograms or pounds. While the body fat percentage
        measures the amount of fat your body holds. Burning body fat can be a result of the accumulated muscles. Muscles
        are like engines when it comes to consuming energy</p>
        <p>Formula</p>
        <p>fat free mass = weight [kg] * (1 - (body fat [%]/ 100)) expressed in kilogram units [kg].</p>
        <p>FFMI = fat free mass [kg]/ (height [m])². Expressed in kilograms per square meter [kg/m²].</p>`,
  },
  {
    id: 'VisceralFat',
    numeric: true,
    disablePadding: false,
    label: 'Visceral Fat (%)',
    title: `<p>The normal visceral fat range should be about 10% of your body fat.
         You can figure out your visceral fat level by calculating your total body fat percentage and then taking off 10%.</p>`,
  },
  {
    id: 'SubcutaneousFat',
    numeric: true,
    disablePadding: false,
    label: 'Subcutaneous Fat (%)',
    title: `<p>For most people, subcutaneous fat makes up about 90% of their body fat. The remaining 10% makes up visceral fat.</p>`,
  },
];

function EnhancedTableHead(props) {
  const {
    // onSelectAllClick,
    order,
    orderBy,
    // numSelected,
    // rowCount,
    onRequestSort,
    showdata,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  let headcell =
    showdata === 'Obesity'
      ? headCells
      : showdata === 'Sickle Cell'
      ? headCells1
      : headCells2;

  return (
    <TableHead>
      <TableRow>
        {headcell.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {/* {headCell.label} */}
              {headCell.title !== '' ? (
                <Tooltip
                  title={
                    <Typography
                      color="inherit"
                      dangerouslySetInnerHTML={{ __html: headCell.title }}
                    />
                    // { headCell.title}
                    // </Typography>
                  }
                >
                  <i
                    className="fas fa-info-circle finpoint"
                    style={{ marginLeft: '5px' }}
                  ></i>
                </Tooltip>
              ) : null}

              <p>{headCell.label}</p>

              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  // numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  // onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  // rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        ></Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <></>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};
// const useStyles = makeStyles((theme) => ({

//     tableCell: {
//       borderRightStyle: "solid",
//       borderRightColor: "black",
//       display: "tableRowGroup"
//     }
//   }));

export default function EnhancedTable(props) {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const selected = [];
  const [page, setPage] = React.useState(0);
  const dense = true;
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const rows = props.data;
  // console.log(rows)
  const { viewDetails, showdata } = props;
  // const classes = useStyles();
  // console.log(classes)
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              showdata={showdata}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.PatientId);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  // console.log(row.Total_SCORE);
                  return (
                    <TableRow
                      // hover
                      // onClick={() => viewDetails(row.PatientId, row.Gender)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={`${row.AppointmentId}`}
                      selected={isItemSelected}
                      className="finpoint"
                    >
                      {showdata === 'Obesity' ? (
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          // className={classes.tableCell}
                          onClick={() =>
                            viewDetails(
                              row.PatientName.toUpperCase(),
                              row.Gender
                            )
                          }
                          style={{
                            textDecoration: 'underline',
                          }}
                        >
                          {row.PatientName.toUpperCase()}
                        </TableCell>
                      ) : (
                        <TableCell component="th" id={labelId} scope="row">
                          {row.PatientName.toUpperCase()}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {moment(row.TestDate).format('DD/MM/YYYY')}
                      </TableCell>
                      {/* <TableCell align="right">{row.PatientPhone}</TableCell> */}
                      <TableCell align="right">{row.age}</TableCell>
                      <TableCell align="right">{row.Gender}</TableCell>
                      {showdata === 'Sickle Cell' ||
                      showdata === 'Non-Invasive' ||
                      showdata === 'Hemoglobin' ? (
                        <TableCell align="right">
                          {row.SickleCell_SOL}
                        </TableCell>
                      ) : null}
                      {showdata === 'Sickle Cell' ||
                      showdata === 'Non-Invasive' ||
                      showdata === 'Hemoglobin' ? (
                        <TableCell align="right">
                          {row.SickleCell_SCORE_TEXT}
                        </TableCell>
                      ) : null}
                      {showdata === 'Sickle Cell' ||
                      showdata === 'Non-Invasive' ||
                      showdata === 'Hemoglobin' ? (
                        <TableCell align="right">{row.HEMOGLOBIN}</TableCell>
                      ) : null}
                      {showdata === 'Sickle Cell' ||
                      showdata === 'Non-Invasive' ||
                      showdata === 'Hemoglobin' ? (
                        <TableCell align="right">
                          {row?.HealthScore?.toFixed(2)}
                        </TableCell>
                      ) : null}
                      {showdata === 'Obesity' ||
                      showdata === 'Non-Invasive' ||
                      showdata === 'Hemoglobin' ? (
                        <TableCell align="right">
                          {row?.Total_SCORE?.toFixed(2)}
                        </TableCell>
                      ) : null}
                      {showdata === 'Obesity' ||
                      showdata === 'Non-Invasive' ||
                      showdata === 'Hemoglobin' ? (
                        <TableCell align="right">{row.Weight}</TableCell>
                      ) : null}
                      {showdata === 'Obesity' ||
                      showdata === 'Non-Invasive' ||
                      showdata === 'Hemoglobin' ? (
                        <TableCell align="right">{row.Height}</TableCell>
                      ) : null}
                      <TableCell align="right">{row.BMI}</TableCell>
                      <TableCell align="right">{row.BMR}</TableCell>
                      <TableCell align="right">{row.LBM}</TableCell>
                      <TableCell align="right">{row.BodyFatWeight}</TableCell>
                      <TableCell align="right">{row.MuscleMass}</TableCell>
                      <TableCell align="right">{row.VisceralFat}</TableCell>
                      <TableCell align="right">{row.SubcutaneousFat}</TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={12} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
